import React, { useState } from 'react'
import clsx from 'clsx'
import { useSelector, useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Collapse from '@material-ui/core/Collapse'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { navItems } from 'application/routes'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import { I18n } from '@aws-amplify/core'

const I18N_NS = 'nav'

I18n.putVocabularies({
  en: {
    [`${I18N_NS}.link.developer.documentation`]: 'Developer Documentation'
  }
})

const useStyles = makeStyles(theme => ({
  nested: {
    paddingLeft: `${theme.spacing(9)}px !important`
  },
  // Styles for the Collapse wrapper element. Used to give
  // a darker background to collapsed menus.
  wrapper: {
    backgroundColor: theme.palette.background.offset
  },
  listPadding: {
    paddingTop: theme.spacing(0),
    '& > div > .MuiListItem-gutters': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2)
    },
    '& .MuiListItem-gutters': {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4)
    }
  }
}))

const Nav = ({ items, currentPath }) => {
  const classes = useStyles()

  const trim = (val) => val.replace(/^\//, '')

  const debugMode = useSelector((state) => (state._user.debugMode))

  // Check if path is selected
  const isSelected = (path, exact = false) => {
    // Include more complex comparisons to automatically find nested paths and to also cater for / (home)
    return (path && trim(path) && RegExp(`^${trim(path)}${exact ? '$' : ''}`).test(trim(currentPath))) || (path === currentPath)
  }

  // Find first selected entry in the nav
  const selected = items.find((i) => isSelected(i.path, i.exact))

  //Get the companyType
  const companyType = useSelector((state) => {
    const type = state._user.impersonate?.companyType ?? state.auth.payload?.companyType;
    return type;
  });
  // Set up initial "open" menu values
  const [open, setOpen] = useState(selected && (selected.parent || selected.id))

  /**
   * @param {object} item - The nav row
   */
  const toggleNav = (item) => {
    // If no path, collapse children
    const id = item.parent || item.id
    setOpen(open === id ? null : id)
  }

  // First figure out what's selected, since this also selects the parent...we kind
  // of have to do this before our rendering.
  items = items.map((item) => {
    return { ...item, selected: isSelected(item.path, item.exact) }
  })

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flexGrow={1}>
        {/* dense=true forces the usage of body2 text */}
        <List
          dense
          component="nav"
          classes={{ padding: classes.listPadding }}>
          <Box height={45} bgcolor="#22282e" display="flex" flexDirection="column" justifyContent="center">
            <Typography align="center">View</Typography>
          </Box>
          {items.filter((i) => !i.parent).slice(0, 4).map((item, index) => {
            const children = items.filter((i) => (item.id && i.parent === item.id))
            const Icon = item.icon
            const opened = (item.id && open === item.id)
            const selectedChildren = Boolean(children.filter((i) => i.selected).length)

            return (
              <Box key={`nav-${index}`} className={clsx({ [classes.wrapper]: opened })}>
                <ListItem button component="a" href={Boolean(item.path) ? item.path : null} onClick={Boolean(item.path) ? null : () => toggleNav(item)} disabled={item.disabled} selected={item.selected || selectedChildren}>
                  <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={item.label} />
                  {Boolean(children.length) ? (opened ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />) : null}
                </ListItem>
                {Boolean(children.length) &&
                  <Collapse in={opened} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense={true}>
                      {children.map((item, index) => {
                        if (item.admin && debugMode) {
                          return (
                            <ListItem key={`subnav-${index}`} button component="a" href={item.path} disabled={item.disabled} selected={item.selected} className={classes.nested}>
                              <ListItemText primary={item.label} />
                            </ListItem>
                          )
                        } else if (!item.admin) {
                          return (
                            <ListItem key={`subnav-${index}`} button component="a" href={item.path} disabled={item.disabled} selected={item.selected} className={classes.nested}>
                              <ListItemText primary={item.label} />
                            </ListItem>
                          )
                        }
                      })}
                    </List>
                  </Collapse>
                }
              </Box>
            )
          })}
          <Box marginTop={5} height={45} bgcolor="#22282e" display="flex" flexDirection="column" justifyContent="center">
            <Typography align="center">Manage</Typography>
          </Box>
          {items.filter((i) => !i.parent).slice(4).map((item, index) => {
            const children = items.filter((i) => (item.id && i.parent === item.id))
            const Icon = item.icon
            const opened = (item.id && open === item.id)
            const selectedChildren = Boolean(children.filter((i) => i.selected).length)

            return (
              <Box key={`nav-${index}`} className={clsx({ [classes.wrapper]: opened })}>
                <ListItem button component="a" href={Boolean(item.path) ? item.path : null} onClick={Boolean(item.path) ? null : () => toggleNav(item)} disabled={item.disabled} selected={item.selected || selectedChildren}>
                  <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={item.label} />
                  {Boolean(children.length) ? (opened ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />) : null}
                </ListItem>
                {Boolean(children.length) &&
                  <Collapse in={opened} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding dense={true}>
                      {children.map((item, index) => {
                        return (
                          <ListItem key={`subnav-${index}`} button component="a" href={item.path} disabled={item.disabled} selected={item.selected} className={classes.nested}>
                            <ListItemText primary={item.label} />
                          </ListItem>
                        )
                      })}
                    </List>
                  </Collapse>
                }
              </Box>
            )
          })}
        </List>
      </Box>
      <Box>
        <Divider />
        <Box p={2} textAlign="center">
          <Typography variant="body2">
            <Link href="https://docs.ergosense.io" target="_blank">
              {I18n.get(`${I18N_NS}.link.developer.documentation`)}
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

Nav.defaultProps = {
  items: []
}

Nav.propTypes = {
  items: PropTypes.array.isRequired
}

export default Nav

export const Connected = (props) => {
  const dispatch = useDispatch()
  const companyType = useSelector((state) => state._user.impersonate?.companyType || state.auth.payload?.companyType || []);
  const isAdmin = useSelector((state) => ((state.auth.payload['cognito:groups'] || []).find((val) => val === "admin") || []).length)

  // Start with all nav items
  let filteredNavItems = navItems;

  // Filter out Terrasense items if not Terrasense company
  if (String(companyType).trim() !== "3") {
    filteredNavItems = filteredNavItems.filter((item) => !item.terrasense);
  }

  // Filter out Ergosense items if not Ergosense company
  if (String(companyType).trim() !== "2" && String(companyType).trim() !== "1") {
    filteredNavItems = filteredNavItems.filter((item) => !item.ergosense);
  }

  // Filter admin items
  filteredNavItems = filteredNavItems.filter((item) => !item.admin || (item.admin && isAdmin))



  props = {
    ...props,
    currentPath: useSelector((state) => state.router.location.pathname),
    items: filteredNavItems,
    onNavigate: (path) => dispatch(push(path))
  }

  return <Nav {...props} />
}
